import React, { useState, useEffect } from "react";
import axios from "../api/axios";
import { useAuth } from "../context/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import './styles/UserProfile.css';
import config from "../config/config";
import SEO from "../seo/SEO";

const UserProfile = () => {
  const { auth, loading } = useAuth();
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showOTPField, setShowOTPField] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [twoFAType, setTwoFAType] = useState("email");

  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);
  const [verificationCodeDigits, setVerificationCodeDigits] = useState(["", "", "", "", "", ""]);
  
  const handleVerificationDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // only digits, single char
    const updatedDigits = [...verificationCodeDigits];
    updatedDigits[index] = value;
    setVerificationCodeDigits(updatedDigits);
  
    if (value && index < 5) {
      const nextInput = document.querySelector(`#verify-otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };
  


  const [backupEmails, setBackupEmails] = useState([]);
  const [newBackupEmail, setNewBackupEmail] = useState("");

  const verifyBackupEmail = async () => {
    const code = verificationCodeDigits.join("");
    if (code.length !== 6 || !pendingVerificationEmail) return;
  
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      const res = await axios.post("/api/2fa/verify-backup-email", {
        email: pendingVerificationEmail,
        code
      }, {
        headers: { "x-csrf-token": csrfToken }
      });
  
      if (res.data.success) {
        setVerificationCodeDigits(["", "", "", "", "", ""]);
        setPendingVerificationEmail(null);
        fetchBackupEmails();
      } else {
        console.error("Verification failed:", res.data.error);
      }
    } catch (err) {
      console.error("Failed to verify backup email:", err);
    }
  };
  


  const fetchBackupEmails = async () => {
    try {
      const res = await axios.get("/api/2fa/backup-emails");
      if (res.data.success) {
        setBackupEmails(res.data.emails); // populate state
      }
    } catch (err) {
      console.error("Failed to fetch backup emails:", err);
    }
  };
  
  
  const addBackupEmail = async () => {
    if (!newBackupEmail) return;
  
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      if (!csrfToken) {
        console.error("🚨 Missing CSRF token. Request blocked.");
        return;
      }
  
      const { data } = await axios.post(
        "/api/2fa/backup-emails",
        { email: newBackupEmail },
        {
          withCredentials: true,
          headers: {
            "x-csrf-token": csrfToken
          }
        }
      );
  
      if (data.success) {
        setNewBackupEmail("");
        setPendingVerificationEmail(newBackupEmail); // trigger UI to show OTP box
        fetchBackupEmails();
      }
  
    } catch (err) {
      console.error("❌ Failed to add 2FA backup email:", err);
    }
  };
  
  
  const removeBackupEmail = async (email) => {
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      await axios.delete("/api/2fa/backup-emails", {
        data: { email },
        headers: { "x-csrf-token": csrfToken },
        withCredentials: true
      });
  
      fetchBackupEmails(); // Refresh the list
    } catch (err) {
      console.error("Failed to delete backup email:", err);
    }
  };
  
  
  const resendVerification = async (email) => {
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      await axios.post("/api/2fa/resend-backup-verification", { email }, {
        headers: { "x-csrf-token": csrfToken },
        withCredentials: true
      });
  
      // 👇 Set this so the OTP field shows up
      setPendingVerificationEmail(email);
      setVerificationCodeDigits(["", "", "", "", "", ""]);
  
      alert("Verification code has been resent.");
    } catch (err) {
      console.error("❌ Resend failed:", err);
      alert("Failed to resend verification code.");
    }
  };
  
  
  

  useEffect(() => {
    if (twoFactorEnabled && twoFAType === "email") {
      fetchBackupEmails();
    }
  }, [twoFactorEnabled, twoFAType]);
  


  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otp = otpDigits.join(""); // Combine for backend submission


  const [confirmPassword, setConfirmPassword] = useState(""); // ✅ New state


  const [password, setPassword] = useState("");
  const [showBackupRegeneration, setShowBackupRegeneration] = useState(false);

  const [disable2FAPassword, setDisable2FAPassword] = useState("");
  const [showDisable2FAPasswordField, setShowDisable2FAPasswordField] = useState(false);



  const navigate = useNavigate();

  useEffect(() => {
    if (auth.user) {
      //console.log("🔍 Auth User Data in UserProfile:", auth.user);
      
      setFormData({ name: auth.user.name, email: auth.user.email, password: "" });
      setTwoFactorEnabled(auth.user.twofactorRequired); // ✅ Correctly check if 2FA is enabled
      setTwoFAType(auth.user.twoFactorMethod || "email"); // ✅ Set 2FA type from auth
    }
  }, [auth.user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");
  
    if (formData.password && formData.password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      return;
    }
  
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      if (!csrfToken) {
        console.error("🚨 Missing CSRF token. Request blocked.");
        setErrorMessage("Failed to update profile due to security validation.");
        return;
      }
  
      await axios.put("/api/user/me", formData, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken }
      });
  
      setTimeout(() => window.location.reload(), 500);
      setSuccessMessage("Profile updated successfully! ✅");
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Update failed.");
    }
  };
  


  const enable2FA = async () => {
    try {
        // ✅ Retrieve CSRF token from cookies
        const csrfToken = document.cookie
            .split("; ")
            .find(row => row.startsWith("csrfToken="))
            ?.split("=")[1];

        if (!csrfToken) {
            console.error("🚨 Missing CSRF token. Request blocked.");
            setErrorMessage("Failed to enable 2FA due to security validation.");
            return;
        }

        let res;
        if (twoFAType === "email") {
            res = await axios.post("/api/2fa/send-2fa-email", {}, {
                withCredentials: true,
                headers: { "x-csrf-token": csrfToken }
            });
        } else {
            // ✅ Send email along with the request
            res = await axios.post("/api/2fa/enable-2fa", { email: formData.email }, {
                withCredentials: true,
                headers: { "x-csrf-token": csrfToken }
            });
        }

        setQrCode(res.data.qrCode);
        setSecret(res.data.secret);
        setShowOTPField(true);
    } catch (error) {
        setErrorMessage(error.response?.data?.error || "Failed to enable 2FA.");
    }
};

const handleOtpChange = (value, index) => {
  if (!/^\d?$/.test(value)) return;
  const updatedDigits = [...otpDigits];
  updatedDigits[index] = value;
  setOtpDigits(updatedDigits);

  if (value && index < 5) {
    document.getElementById(`otp-${index + 1}`).focus();
  }
};

const handleOtpPaste = (e) => {
  e.preventDefault();
  const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

  if (pasteData.length === 6) {
    const updatedDigits = pasteData.split('');
    setOtpDigits(updatedDigits);
    document.getElementById('otp-5').focus();
  }
};


const handleGenericOtpPaste = (e, setDigits, prefix) => {
  e.preventDefault();
  const pasteData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);

  if (pasteData.length === 6) {
    const updatedDigits = pasteData.split("");
    setDigits(updatedDigits);

    // Delay focus to allow state update
    setTimeout(() => {
      const lastInput = document.getElementById(`${prefix}-5`);
      if (lastInput) lastInput.focus();
    }, 0);
  }
};


const handleOtpKeyDown = (e, index) => {
  if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
    const updatedDigits = [...otpDigits];
    updatedDigits[index - 1] = "";
    setOtpDigits(updatedDigits);
    document.getElementById(`otp-${index - 1}`).focus();
  }
};


const verifyOTP = async () => {
  try {
      const csrfToken = document.cookie
          .split("; ")
          .find(row => row.startsWith("csrfToken="))
          ?.split("=")[1];

      if (!csrfToken) {
          console.error("🚨 Missing CSRF token. Request blocked.");
          setErrorMessage("Failed to verify 2FA due to security validation.");
          return;
      }

      const endpoint = twoFAType === "email" 
          ? "/api/2fa/verify-email-2fa" 
          : "/api/2fa/verify-2fa";

      const res = await axios.post(endpoint, { otp }, {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken }
      });

      setShowOTPField(false);
      setSuccessMessage("Two-Factor Authentication enabled successfully!");

      if (twoFAType === "app") {
          navigate("/user/backup-codes", {
              state: { backupCodes: res.data.backupCodes }
          });
      } else {
          setTimeout(() => window.location.reload(), 500);
      }
  } catch (error) {
      setErrorMessage(error.response?.data?.error || "Invalid OTP.");
  }
};


const regenerateBackupCodes = async () => {
  setErrorMessage("");
  setSuccessMessage("");

  try {
    const csrfToken = document.cookie
      .split("; ")
      .find(row => row.startsWith("csrfToken="))
      ?.split("=")[1];

    if (!csrfToken) {
      setErrorMessage("Failed to regenerate backup codes due to security validation.");
      return;
    }

    const res = await axios.post(
      "/api/2fa/regenerate-backup-codes",
      { password },
      {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      }
    );

    if (res.data.backupCodes) {
      navigate("/user/backup-codes", { state: { backupCodes: res.data.backupCodes } }); // ✅ Pass codes here
    }
  } catch (error) {
    setErrorMessage(error.response?.data?.error || "Failed to regenerate backup codes.");
  }
};



  

const disable2FA = async () => {
  try {
      if (!disable2FAPassword) {
          setErrorMessage("Password is required to disable 2FA.");
          return;
      }

      const csrfToken = document.cookie
          .split("; ")
          .find(row => row.startsWith("csrfToken="))
          ?.split("=")[1];

      if (!csrfToken) {
          console.error("🚨 Missing CSRF token. Request blocked.");
          setErrorMessage("Failed to disable 2FA due to security validation.");
          return;
      }

      await axios.post("/api/2fa/disable-2fa", { password: disable2FAPassword }, {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken }
      });

      setSuccessMessage("Two-Factor Authentication disabled.");
      setTimeout(() => window.location.reload(), 500);
  } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to disable 2FA.");
  }
};


if (loading) {
  return (
    <div className="user-profile">
      <h2>My Profile</h2>
      <div className="spinner"></div>
      <p>Loading your profile...</p>
    </div>
  );
}


  return (
    <>
        <SEO
            robots="noindex, nofollow"
            title="My Profile - Withrow Knives"
            description="Manage your Withrow Knives account, update profile details, and enhance security with two-factor authentication."
            keywords={[
                "user profile",
                "account settings",
                "update profile",
                "manage account",
                "Withrow Knives login",
                "two-factor authentication"
            ]}
            url={`${config.metadata.siteUrl}/user/profile`}
            jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "User Profile - Withrow Knives",
                "url": `${config.metadata.siteUrl}/user/profile`,
                "description": "Access and manage your Withrow Knives account. Update your personal details, manage orders, and enhance security with two-factor authentication.",
                "breadcrumb": {
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": config.metadata.siteUrl
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": "User",
                            "item": `${config.metadata.siteUrl}/user`
                        },
                        {
                            "@type": "ListItem",
                            "position": 3,
                            "name": "Profile",
                            "item": `${config.metadata.siteUrl}/user/profile`
                        }
                    ]
                },
                "mainEntityOfPage": {
                    "@type": "WebPage",
                    "@id": `${config.metadata.siteUrl}/user/profile`
                }
            }}
        />



      <div className="user-profile">
  <h2>My Profile</h2>

  {successMessage && <p className="success" aria-live="polite">{successMessage}</p>}
  {errorMessage && <p className="error" aria-live="polite">{errorMessage}</p>}

  <div className="orders-section">
    <h3>My Orders</h3>
    <Link to="/user/orders">
      <button><i className="fa-solid fa-bag-shopping"></i> View My Orders</button>
    </Link>
  </div>

  {/* ✅ Profile Update Form */}
  
  <form onSubmit={handleSubmit} className="update-profile-form">
    <h3>Update Profile</h3>
    <label htmlFor="profile-name">Full Name</label>
    <input 
      id="profile-name"
      type="text" 
      name="name" 
      value={formData.name} 
      onChange={handleChange} 
      placeholder="Name" 
      required 
    />

    <label htmlFor="profile-email">Email</label>
    <input 
      id="profile-email"
      type="email" 
      name="email" 
      value={formData.email} 
      onChange={handleChange} 
      placeholder="Email" 
      required 
      disabled 
    />

    <label htmlFor="new-password">New Password (optional)</label>
    <input
      id="new-password"
      type="password"
      name="password"
      placeholder="New Password (optional)"
      onChange={handleChange}
    />

    <label htmlFor="confirm-password">Confirm New Password (optional)</label>
    <input
      id="confirm-password"
      type="password"
      placeholder="Confirm New Password (optional)"
      onChange={(e) => setConfirmPassword(e.target.value)}
    />

    <button type="submit">Update Profile</button>
  </form>

  {/* ✅ Two-Factor Authentication Section */}
  <div className="two-factor-section">
    <h3>Two-Factor Authentication</h3>

    {!twoFactorEnabled ? (
      <>
        <div className="enable-2fa-wrapper">
          <label className={twoFAType === "email" ? "selected" : ""}>
            <input
              type="radio"
              value="email"
              checked={twoFAType === "email"}
              onChange={(e) => setTwoFAType(e.target.value)}
            />
            <i className="fa-solid fa-envelope"></i>
            <span>Enable Email-Based 2FA</span>
          </label>
          <label className={twoFAType === "app" ? "selected" : ""}>
            <input
              type="radio"
              value="app"
              checked={twoFAType === "app"}
              onChange={(e) => setTwoFAType(e.target.value)}
            />
            <i className="fa-solid fa-qrcode"></i>
            <span>Enable Authenticator App 2FA</span>
          </label>
        </div>
        <button className='two-factor-button' onClick={enable2FA}>Enable 2FA</button>
      </>
    ) : (
      <>
        <p><strong>2FA is enabled using: {twoFAType === "app" ? "Authenticator App" : "Email OTP"}</strong></p>
        {!showDisable2FAPasswordField ? (
            <>
              <button className="disable-2fa-button" onClick={() => setShowDisable2FAPasswordField(true)}>
                <i className="fa-solid fa-user-slash"></i> Disable 2FA
              </button>
            </>
          ) : (
            <>
              <label htmlFor="disable-2fa-password">Enter your password</label>
              &nbsp;
              <input
                id="disable-2fa-password"
                type="password"
                value={disable2FAPassword}
                onChange={(e) => setDisable2FAPassword(e.target.value)}
                placeholder="Enter your password"
              />
              &nbsp;
              &nbsp;
              <button onClick={disable2FA} disabled={!disable2FAPassword}>
                Confirm & Disable 2FA
              </button>
              
            </>
          )}
      </>
    )}
    
    {twoFactorEnabled && twoFAType === "email" && (
      <div className="additional-2fa-emails">
        <h4>Backup Emails for 2FA</h4>
        <p>You can add up to 3 emails to receive 2FA codes at login, in case your primary is unavailable.</p>

        {/* Add new email input */}
        {backupEmails.length < 3 && (
          <div className="add-backup-email">
            <input
              type="email"
              placeholder="Add backup 2FA email"
              value={newBackupEmail}
              onChange={(e) => setNewBackupEmail(e.target.value)}
            />
            &nbsp;
            &nbsp;
            <button onClick={addBackupEmail}>Add Email</button>
          </div>
        )}

        <table className="backup-emails-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {backupEmails.map((emailObj, index) => (
              <tr key={index}>
                <td>{emailObj.email}</td>
                <td>
                  {emailObj.verified ? (
                    <span className="status-tag verified">Verified</span>
                  ) : (
                    <span className="status-tag unverified">Unverified</span>
                  )}
                </td>
                <td className="backup-email-actions">
                  {!emailObj.verified && (
                    <button
                      onClick={() => resendVerification(emailObj.email)}
                      className="action-btn resend-btn"
                    >
                      Resend Code
                    </button>
                  )}
                  <button
                    onClick={() => removeBackupEmail(emailObj.email)}
                    className="action-btn delete-btn"
                    title="Remove Email"
                  >
                    <i className="fa-solid fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {pendingVerificationEmail && (
          <div className="verify-2fa-wrapper">
            <p>
              A verification code has been sent to <strong>{pendingVerificationEmail}</strong>. 
              Please enter it below to confirm this email.
            </p>

            <div className="otp-container" onPaste={(e) => handleGenericOtpPaste(e, setVerificationCodeDigits, "verify-digit")}>
              {verificationCodeDigits.map((digit, index) => (
                <input
                  key={index}
                  id={`verify-digit-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleVerificationDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(e, index)}
                  className="otp-box"
                />
              ))}
            </div>

            <button
              onClick={verifyBackupEmail}
              disabled={verificationCodeDigits.join("").length !== 6}
            >
              Verify Email
            </button>
          </div>
        )}


      </div>
    )}

    {showOTPField && (
      <div className="verify-2fa-wrapper">
        {twoFAType === "app" && qrCode && (
          <>
            <p>Scan this QR Code with your Authenticator App:</p>
            <img src={qrCode} alt="2FA QR Code" />
            <p>Or enter this code manually in your app: <strong>{secret}</strong></p>
          </>
        )}
        <div className="otp-container" onPaste={handleOtpPaste}>
          {otpDigits.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              inputMode="numeric"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOtpChange(e.target.value, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              className="otp-box"
            />
          ))}
        </div>
        <button onClick={verifyOTP} disabled={otp.length !== 6}>
          Verify OTP
        </button>
      </div>
    )}

    {twoFactorEnabled && twoFAType === "app" && (
      <>
        {!showBackupRegeneration ? (
          <>
          &nbsp;
          <br/>
          <br/>
          <button onClick={() => setShowBackupRegeneration(true)}>
            <i className="fa-solid fa-arrows-rotate"></i> Regenerate Backup Codes
          </button>
          </>
        ) : (
          <>
            <br/>
            <br/>
            <label htmlFor="backup-password">Enter your password</label>
            &nbsp;
            <input
              id="backup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
            />
            &nbsp;
            &nbsp;
            <button onClick={regenerateBackupCodes}>Confirm & Generate New Codes</button>
          </>
        )}
      </>
    )}

  </div>
</div>

    </>
  );
};

export default UserProfile;
